import { useEffect, useState } from "react";
import { api } from "@/lib/nexus";
import { useAuth } from "@/context/AuthContext";
import { Cpu, Plus, Trash2, ShieldCheck, X, Loader2, Server } from "lucide-react";

const PROVIDERS = [
  { key: "openai_compat", label: "OpenAI-Compatible (vLLM · Ollama · Together · Groq · OpenRouter · self-hosted)" },
  { key: "anthropic", label: "Anthropic (bring-your-own key)" },
];

const PURPOSES = [
  { key: "correlation", label: "Correlation" },
  { key: "vulnerability", label: "Vulnerability Finding" },
  { key: "intent", label: "Intent Analysis" },
];

export default function Models() {
  const { canWrite } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "", provider: "openai_compat", base_url: "", api_key: "", model_id: "", purpose: "correlation",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/models").then((r) => { setItems(r.data.items); setLoading(false); });
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/models", form);
      setShowForm(false);
      setForm({ name: "", provider: "openai_compat", base_url: "", api_key: "", model_id: "", purpose: "correlation" });
      load();
    } finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this model?")) return;
    await api.delete(`/models/${id}`);
    load();
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="panel-title">MODEL REGISTRY // BRING YOUR OWN MODEL</div>
          <h1 className="font-rajdhani font-bold text-2xl tracking-tight mt-1">
            Plug in <span className="text-nexus-cyan">any OpenAI-compatible model</span> or Anthropic key
          </h1>
          <p className="text-[13px] text-nexus-text-2 mt-1 max-w-2xl">
            Registered models appear in the agentic-action model selector — investigate / research / test-transaction /
            pen-test can be delegated to your own vulnerability-finding or intent-analysis model.
          </p>
        </div>
        {canWrite && (
          <button
            data-testid="add-model-btn"
            onClick={() => setShowForm(true)}
            className="text-[10px] font-mono uppercase tracking-widest text-nexus-cyan border border-nexus-cyan/50 hover:bg-nexus-cyan/10 px-3 py-1.5 flex items-center gap-1"
          >
            <Plus size={12} /> Add Model
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-nexus-text-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((m) => (
            <div key={m.id} data-testid={`model-card-${m.id}`} className="panel p-3 brackets">
              <span className="b1" /><span className="b2" />
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {m.provider === "anthropic" ? <ShieldCheck size={13} className="text-nexus-violet" /> : <Server size={13} className="text-nexus-cyan" />}
                    <div className="font-rajdhani font-semibold uppercase tracking-widest text-[13px] truncate">{m.name}</div>
                    {m.builtin && <span className="text-[9px] font-mono uppercase tracking-widest border border-nexus-cyan/50 text-nexus-cyan px-1">built-in</span>}
                  </div>
                  <div className="hash mt-1 truncate">{m.model_id}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-3 mt-1">
                    {PROVIDERS.find((p) => p.key === m.provider)?.label.split(" (")[0] || m.provider}
                  </div>
                  {m.base_url && <div className="text-[10px] font-mono text-nexus-text-2 mt-1 break-all">{m.base_url}</div>}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {m.purpose && <span className="text-[9px] font-mono uppercase tracking-widest border border-nexus-amber/50 text-nexus-amber px-1.5 py-0.5">{m.purpose}</span>}
                    {m.api_key && <span className="text-[9px] font-mono text-nexus-text-3">key {m.api_key}</span>}
                  </div>
                </div>
                {!m.builtin && canWrite && (
                  <button data-testid={`delete-model-${m.id}`} onClick={() => remove(m.id)} className="text-nexus-text-3 hover:text-nexus-rose shrink-0" aria-label="Delete model">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="panel brackets max-w-lg w-full p-6 space-y-3">
            <span className="b1" /><span className="b2" />
            <div className="flex justify-between items-start">
              <div>
                <div className="panel-title"><Cpu size={12} className="inline mr-1" />Register Model</div>
                <h2 className="font-rajdhani font-bold text-xl tracking-widest uppercase mt-1">New Model</h2>
              </div>
              <button type="button" onClick={() => setShowForm(false)} className="text-nexus-text-3 hover:text-nexus-text"><X size={16} /></button>
            </div>

            <label className="block">
              <span className="panel-title">Display name</span>
              <input data-testid="model-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full bg-nexus-bg border border-nexus-border px-2 py-1.5 text-[12px] font-mono focus:border-nexus-cyan/60 outline-none" />
            </label>

            <label className="block">
              <span className="panel-title">Provider</span>
              <select data-testid="model-provider-select" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="mt-1 w-full bg-nexus-bg border border-nexus-border px-2 py-1.5 text-[12px] font-mono">
                {PROVIDERS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </label>

            {form.provider === "openai_compat" && (
              <label className="block">
                <span className="panel-title">Base URL</span>
                <input data-testid="model-base-url-input" placeholder="https://api.together.xyz/v1  ·  http://localhost:8080/v1" required={form.provider === "openai_compat"} value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} className="mt-1 w-full bg-nexus-bg border border-nexus-border px-2 py-1.5 text-[12px] font-mono focus:border-nexus-cyan/60 outline-none" />
              </label>
            )}

            <label className="block">
              <span className="panel-title">Model ID</span>
              <input data-testid="model-id-input" placeholder="meta-llama/Llama-3.3-70B-Instruct  ·  claude-opus-4-7  ·  gpt-5.4" required value={form.model_id} onChange={(e) => setForm({ ...form, model_id: e.target.value })} className="mt-1 w-full bg-nexus-bg border border-nexus-border px-2 py-1.5 text-[12px] font-mono focus:border-nexus-cyan/60 outline-none" />
            </label>

            <label className="block">
              <span className="panel-title">API key</span>
              <input data-testid="model-api-key-input" type="password" required value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} className="mt-1 w-full bg-nexus-bg border border-nexus-border px-2 py-1.5 text-[12px] font-mono focus:border-nexus-cyan/60 outline-none" />
            </label>

            <label className="block">
              <span className="panel-title">Purpose</span>
              <select data-testid="model-purpose-select" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="mt-1 w-full bg-nexus-bg border border-nexus-border px-2 py-1.5 text-[12px] font-mono">
                {PURPOSES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-2 border border-nexus-border px-3 py-1.5">Cancel</button>
              <button data-testid="submit-model-btn" type="submit" disabled={submitting} className="text-[10px] font-mono uppercase tracking-widest text-nexus-cyan border border-nexus-cyan/50 hover:bg-nexus-cyan/10 px-3 py-1.5 disabled:opacity-50 flex items-center gap-1">
                {submitting && <Loader2 size={11} className="animate-spin" />} Register
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
